/**
 * Arrow IPC decode — turns QV's binary WebSocket/HTTP payloads into columnar
 * bundles keyed by column / alias name.
 *
 * Two layers:
 *  - {@link decodeArrowTable} — one Arrow IPC table (a single ChartGroup's
 *    polars DataFrame) → {@link ChartGroupColumns}.
 *  - {@link decodeBundle} — the QV `_bundle` binary envelope (LayoutId header +
 *    per-group ChartGroupId/status/length framing) → {@link ColumnarBundle}.
 *
 * Arrow-JS `Table` transparently stitches multiple record batches, so a table
 * split across batches decodes as one contiguous set of columns.
 */

import {
  Type,
  tableFromIPC,
  type DataType,
  type Table,
  type Vector,
} from "apache-arrow";
import {
  TIME_COLUMN_NAMES,
  type AskStamp,
  type ChartGroupColumns,
  type ColumnarBundle,
  type PerfSharedRead,
  type PerfStage,
  type ServerPerf,
  type XAxisKind,
} from "./columns.js";
import { GEOMETRY_METADATA_KEY, parseGeometry } from "./geometry.js";

/** Bytes of the fixed LayoutId header field in the QV bundle envelope. */
const LAYOUT_ID_BYTES = 36;

/**
 * Marks the optional trailing timings block — `"QPRF"`, then a uint32 length,
 * then UTF-8 JSON. See {@link readPerf}.
 */
const PERF_MAGIC = 0x51_50_52_46;

/** Bytes of the magic plus the length that follows it. */
const PERF_HEADER_BYTES = 8;

/** How a single Arrow field participates in the decoded output. */
type FieldRole = "time" | "numeric" | "label" | "skip";

function classifyField(typeId: Type): FieldRole {
  switch (typeId) {
    case Type.Timestamp:
    case Type.Date:
    case Type.Time:
      return "time";
    case Type.Int:
    case Type.Float:
    case Type.Decimal:
    case Type.Bool:
      return "numeric";
    case Type.Utf8:
    case Type.LargeUtf8:
    case Type.Dictionary:
      return "label";
    default:
      return "skip";
  }
}

/**
 * Normalise one time-column cell to unix epoch seconds.
 *
 * Arrow-JS normalises every `Timestamp` unit to milliseconds on `.get()`, so
 * Timestamp cells only need `/1000`. `Date` cells may arrive as a `Date` object
 * or a raw number; both are handled best-effort.
 */
/**
 * One Arrow time value as epoch seconds.
 *
 * **apache-arrow normalises every temporal type to milliseconds on `get()`**,
 * whatever the column declares for storage. Measured on apache-arrow 17, one
 * row per type, all six returning `1722988800000` for 2024-08-07:
 *
 *     Date32<DAY>              1722988800000
 *     Date64<MILLISECOND>      1722988800000
 *     Timestamp<SECOND>        1722988800000
 *     Timestamp<MILLISECOND>   1722988800000
 *     Timestamp<MICROSECOND>   1722988800000
 *     Timestamp<NANOSECOND>    1722988800000
 *
 * So the unit is a fact about the *buffer*, not about what comes out of it, and
 * dividing by a thousand is the whole conversion.
 *
 * Two wrong answers preceded this one, and both are worth naming because both
 * looked reasonable:
 *
 * 1. **A magnitude guess** — `n > 1e12 ? n / 1000 : n * 86_400`, meaning to tell
 *    Date64 milliseconds from Date32 days. 1e12 milliseconds is 2001-09-09, so
 *    every bar older than that took the day-count branch and came out 86.4
 *    million times too large. Found on a weekly AMGN chart reaching back to its
 *    1983 listing: the 1983-11-14 bar decoded as 3.78e16, past the ±8.64e15 ms a
 *    `Date` can hold, so the axis printed "Invalid Date" and `toISOString` threw
 *    `RangeError: Invalid time value` out of a React render.
 *
 * 2. **Trusting the declared unit** — `type.unit === DateUnit.DAY ? n * 86_400
 *    : n / 1000`. Correct-looking, and worse: the server's column really is
 *    `Date32<DAY>`, so this multiplied milliseconds by 86,400 for *every* row
 *    rather than only the pre-2001 ones. It broke the bars the first bug had
 *    left working.
 *
 * The lesson is in the measurement, not the reasoning. Neither wrong version was
 * arrived at by looking at what `get()` actually returns.
 */
export function timeValueToSeconds(raw: unknown, _type: DataType): number {
  if (raw == null) return NaN;
  // Some builds hand back a Date rather than a number; both mean milliseconds.
  if (raw instanceof Date) return raw.getTime() / 1000;
  return Number(raw) / 1000;
}

function readTimeColumn(vec: Vector, type: DataType): number[] {
  const out = new Array<number>(vec.length);
  for (let i = 0; i < vec.length; i++) {
    out[i] = timeValueToSeconds(vec.get(i), type);
  }
  return out;
}

/**
 * One numeric Arrow column as plain numbers, with **nulls as `NaN`**.
 *
 * `.toArray()` returns a TypedArray (Float64Array, Int32Array, BigInt64Array…)
 * spanning every record batch, and `Number` coerces the BigInt cases uniformly.
 * But that is Arrow's *values* buffer, and it does not consult the validity
 * bitmap — so a null reads as whatever occupies its slot, which for a frame
 * Polars serialised is `0.0`.
 *
 * Zero is not a missing value on a price chart. Every indicator has a warm-up:
 * `sma_20` is null for nineteen bars, `rsi_14` for fourteen. Untreated they
 * arrived as a run of zeros, which draws a line to the bottom of the pane and —
 * because the price axis is `EAutoRange.Always` — drags the whole axis down to
 * include them whenever those bars are in view. Verified end to end against
 * bytes from `qf/data.py`, not inferred: `[null, null, 11, 12]` decoded as
 * `[0, 0, 11, 12]`.
 *
 * `NaN` rather than `null` because the result is a `number[]` that feeds
 * SciChart directly, which reads NaN as a break in a series. The bitmap is only
 * consulted when there is one — `nullCount` is zero for every OHLCV column, so
 * the common path is the same single pass it always was.
 */
function readNumericColumn(vec: Vector): number[] {
  const values = Array.from(vec.toArray() as ArrayLike<number | bigint>, Number);
  if (vec.nullCount > 0) {
    for (let i = 0; i < values.length; i++) {
      if (vec.get(i) == null) values[i] = NaN;
    }
  }
  return values;
}

/**
 * Decode one Arrow IPC table (bytes or an already-parsed {@link Table}) into a
 * columnar chart group. The first Timestamp/Date field, or else the first
 * numeric field named like a time column, becomes the x axis; remaining numeric
 * fields become alias columns; string fields become scalar labels.
 *
 * @param input        - Arrow IPC bytes, or a parsed apache-arrow `Table`.
 * @param chartGroupId - Id to stamp on the result (from the bundle header).
 */
export function decodeArrowTable(
  input: Uint8Array | ArrayBuffer | Table,
  chartGroupId = "0",
): ChartGroupColumns {
  const table: Table =
    input instanceof ArrayBuffer || input instanceof Uint8Array
      ? tableFromIPC(input instanceof ArrayBuffer ? new Uint8Array(input) : input)
      : input;

  const columns: Record<string, number[]> = {};
  const labels: Record<string, string> = {};

  let x: number[] | undefined;
  let xKind: XAxisKind = "index";
  let timeFieldChosen = false;

  // Deferred numeric time-column candidate (used only if no real time field).
  let numericTimeName: string | undefined;

  for (const field of table.schema.fields) {
    const name = field.name;
    const vec = table.getChild(name);
    if (!vec) continue;
    const role = classifyField(field.typeId);

    if (role === "time" && !timeFieldChosen) {
      x = readTimeColumn(vec, field.type);
      xKind = "seconds";
      timeFieldChosen = true;
      continue;
    }

    if (role === "numeric") {
      const values = readNumericColumn(vec);
      if (
        !timeFieldChosen &&
        numericTimeName === undefined &&
        TIME_COLUMN_NAMES.includes(name.toLowerCase() as never)
      ) {
        // Remember it as a fallback x, but still expose it as a column.
        numericTimeName = name;
      }
      columns[name] = values;
      continue;
    }

    if (role === "label") {
      const first = vec.length > 0 ? vec.get(0) : null;
      if (first != null) labels[name] = String(first);
      continue;
    }
    // role === "skip" → ignore (e.g. nested/struct columns)
  }

  // No Timestamp/Date field: fall back to a numeric time column, else row index.
  if (!timeFieldChosen) {
    if (numericTimeName !== undefined && columns[numericTimeName]) {
      x = columns[numericTimeName]!.slice();
      xKind = "seconds";
    } else {
      const n = firstColumnLength(columns);
      x = Array.from({ length: n }, (_, i) => i);
      xKind = "index";
    }
  }

  const symbol = labels["symbol"];
  const group: ChartGroupColumns = {
    chartGroupId,
    status: "ok",
    x: x ?? [],
    xKind,
    columns,
    labels,
  };
  if (symbol !== undefined) group.symbol = symbol;
  // Read off the schema, where the server puts what *kind* of table this is
  // (`TAIL_METADATA` in `routers/qv.py`). Only set when true, so a load decodes
  // exactly as it did before the key existed.
  if (table.schema.metadata.get("qv.frame") === "tail") group.tail = true;
  if (table.schema.metadata.get("qv.complete") === "1") group.complete = true;
  const answers = askStamp(table.schema.metadata);
  if (answers) group.answers = answers;
  // The live dot's two facts (intraday charts only; `routers/qv.py` carries
  // them on the load and on every tail).
  const today = table.schema.metadata.get("qv.today");
  if (today === "intra" || today === "rest") group.todaySource = today;
  const phase = table.schema.metadata.get("qv.phase");
  if (phase === "live" || phase === "closed") group.phase = phase;
  const ext = Number(table.schema.metadata.get("qv.ext"));
  const extPhase = table.schema.metadata.get("qv.extPhase");
  if (Number.isFinite(ext) && ext > 0 && (extPhase === "pre" || extPhase === "post")) {
    group.extPrice = ext;
    group.extPhase = extPhase;
  }
  // Shapes, not columns — a pattern plot's whole answer (`geometry.ts`).
  const geometry = parseGeometry(table.schema.metadata.get(GEOMETRY_METADATA_KEY));
  if (geometry) group.geometry = geometry;
  return group;
}

/** `qv.symbol` / `qv.frequency` / `qv.aliases`, whichever the server sent. */
function askStamp(metadata: Map<string, string>): AskStamp | undefined {
  const stamp: AskStamp = {};
  const symbol = metadata.get("qv.symbol");
  if (symbol !== undefined) stamp.symbol = symbol;
  const frequency = metadata.get("qv.frequency");
  if (frequency !== undefined) stamp.frequency = frequency;
  const aliases = metadata.get("qv.aliases");
  if (aliases !== undefined) {
    try {
      const parsed: unknown = JSON.parse(aliases);
      if (Array.isArray(parsed)) stamp.aliases = parsed.map(String);
    } catch {
      // Unreadable is unchecked, never a reason to drop the frame.
    }
  }
  return Object.keys(stamp).length > 0 ? stamp : undefined;
}

function firstColumnLength(columns: Record<string, number[]>): number {
  for (const key of Object.keys(columns)) {
    const col = columns[key];
    if (col) return col.length;
  }
  return 0;
}

/**
 * Decode the QV `_bundle` binary envelope into a {@link ColumnarBundle}.
 *
 * Wire format (all integers big-endian), matching `ws_ind._bundle`:
 * ```
 *   [36 bytes] LayoutId       UTF-8, space-padded / truncated
 *   [ 4 bytes] num_groups     uint32
 *   per group:
 *     [ 4 bytes] ChartGroupId uint32
 *     [ 1 byte ] status       0 = ok (Arrow IPC), 1 = error (UTF-8 string)
 *     [ 4 bytes] data_len     uint32
 *     [ N bytes] payload      Arrow IPC bytes | error text
 * ```
 *
 * @param input - The raw bundle bytes received on the socket.
 */
/**
 * The bundle's trailing timings, or `undefined` when there are none.
 *
 * **Every failure here is `undefined`, never a throw.** This is the block that
 * feeds a diagnostic panel, and a diagnostic must not be able to take the chart
 * down with it — the same lesson `ChartDataInspector`'s `formatX` already
 * carries, where a `toISOString` on a bad timestamp unmounted the React tree
 * from inside the one surface built to show what the server actually sent.
 *
 * Four ways there are no timings, and all four are ordinary:
 *
 * - an older server, which appends nothing;
 * - a truncated stream, where fewer than eight bytes remain;
 * - a magic that does not match, which is the guard that stops a length being
 *   read out of the middle of somebody's Arrow payload;
 * - a length that runs past the end, or JSON that will not parse.
 */
function readPerf(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
): ServerPerfEnvelope | undefined {
  if (bytes.byteLength - offset < PERF_HEADER_BYTES) return undefined;
  if (view.getUint32(offset, false) !== PERF_MAGIC) return undefined;
  const size = view.getUint32(offset + 4, false);
  const start = offset + PERF_HEADER_BYTES;
  if (size === 0 || start + size > bytes.byteLength) return undefined;
  try {
    return JSON.parse(
      new TextDecoder().decode(bytes.subarray(start, start + size)),
    ) as ServerPerfEnvelope;
  } catch {
    return undefined;
  }
}

/** The block's own shape, before it is folded onto each group. */
interface ServerPerfEnvelope {
  v: number;
  source: "request" | "refresh";
  host?: string;
  machine?: string;
  phase?: string;
  builtAtMs: number;
  bundleTotalMs: number;
  beforeBuild?: PerfStage[];
  shared?: PerfSharedRead[];
  groups?: Record<
    string,
    {
      symbol?: string;
      frequency?: string;
      barSource?: string;
      barsRequested?: number;
      barsReturned?: number;
      bytes?: number;
      totalMs?: number;
      stages?: PerfStage[];
    }
  >;
}

/**
 * The bundle's timings for one group, with the bundle-level facts folded in.
 *
 * Folded rather than referenced so a consumer holding one group holds a
 * complete reading: `ChartGroupView` keeps a per-chart snapshot and would
 * otherwise have to carry the whole bundle beside it to say which host answered
 * or whether this was a refresh.
 */
function perfForGroup(
  envelope: ServerPerfEnvelope | undefined,
  chartGroupId: string,
): ServerPerf | undefined {
  if (!envelope) return undefined;
  const group = envelope.groups?.[chartGroupId];
  // A bundle whose timings name no such group still says something worth
  // keeping — which host answered, whether this was a refresh, how long the
  // whole build took — so the record stands with an empty stage list rather
  // than being dropped for the part that is missing.
  return {
    v: envelope.v,
    source: envelope.source,
    host: envelope.host,
    machine: envelope.machine,
    phase: envelope.phase,
    builtAtMs: envelope.builtAtMs,
    // Falls back to the bundle's total only when the group named none — which
    // is the case where the two genuinely are the same number, because there is
    // no group to have spent less than the whole.
    totalMs: group?.totalMs ?? envelope.bundleTotalMs,
    bundleTotalMs: envelope.bundleTotalMs,
    beforeBuild: envelope.beforeBuild ?? [],
    shared: envelope.shared ?? [],
    stages: group?.stages ?? [],
    symbol: group?.symbol,
    frequency: group?.frequency,
    barSource: group?.barSource,
    barsRequested: group?.barsRequested,
    barsReturned: group?.barsReturned,
    bytes: group?.bytes,
  };
}

export function decodeBundle(input: Uint8Array | ArrayBuffer): ColumnarBundle {
  const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder();

  let offset = 0;
  const layoutId = decoder
    .decode(bytes.subarray(offset, offset + LAYOUT_ID_BYTES))
    .replace(/\s+$/u, "");
  offset += LAYOUT_ID_BYTES;

  const numGroups = view.getUint32(offset, false);
  offset += 4;

  const groups: ChartGroupColumns[] = [];
  const framed: Array<{ id: string; status: number; payload: Uint8Array }> = [];
  for (let g = 0; g < numGroups; g++) {
    const chartGroupId = String(view.getUint32(offset, false));
    offset += 4;
    const status = view.getUint8(offset);
    offset += 1;
    const dataLen = view.getUint32(offset, false);
    offset += 4;
    framed.push({
      id: chartGroupId,
      status,
      payload: bytes.subarray(offset, offset + dataLen),
    });
    offset += dataLen;
  }

  // **Read after the groups, because that is where it is** — the loop above
  // ends exactly where an older client stopped looking, which is the whole
  // reason the block can be added to a live wire at all. Read before the Arrow
  // decode below only so the timings can be attached as each group is built.
  const perf = readPerf(bytes, view, offset);

  for (const { id, status, payload } of framed) {
    if (status === 0) {
      const group = decodeArrowTable(payload, id);
      group.serverPerf = perfForGroup(perf, id);
      groups.push(group);
    } else {
      groups.push({
        chartGroupId: id,
        status: "error",
        x: [],
        xKind: "index",
        columns: {},
        labels: {},
        error: decoder.decode(payload),
        // Kept on an error too: a chart that failed is the one somebody opens
        // the panel about, and how long the failure took is part of the answer.
        serverPerf: perfForGroup(perf, id),
      });
    }
  }

  return { layoutId, groups };
}